import "./index.css";

import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { Container, Grid, MenuItem, Select, Typography } from "@material-ui/core";

import { IAppState } from "../../store";
import { GET_OFFERS } from "../../graphql/offers/getOffersQuery";
import { getSignedUrl } from "../../utils/getSignedUrl";
import { useTranslation } from "react-i18next";

interface IOfferCondition {
  field: string;
  operator: string;
  values: string[];
}

interface IOffer {
  id: string;
  name: string;
  description?: string;
  code?: string;
  discountValueType: string;
  discountValue: number;
  validFrom?: string;
  validTo?: string;
  status: string;
  branchIds: string[];
  conditions?: IOfferCondition[];
}

// Discounts & Offers: an offer restricted to a specific booking type
// (e.g. a coupon that only applies to SUBSCRIPTION) shouldn't be shown to a
// customer who has already selected a different booking type on this page —
// showing "20% off your monthly subscription" to someone booking a one-off
// rental (or vice versa) is confusing and the code wouldn't even apply.
const matchesBookingType = (offer: IOffer, bookingType?: string) => {
  const condition = offer.conditions?.find((c) => c.field === "BOOKING_TYPE");
  if (!condition) {
    return true;
  }
  if (!bookingType) {
    return false;
  }
  return condition.values.includes(bookingType);
};

const isCurrentlyValid = (offer: IOffer) => {
  if (offer.status !== "ACTIVE") {
    return false;
  }
  const now = new Date();
  if (offer.validFrom && new Date(offer.validFrom) > now) {
    return false;
  }
  if (offer.validTo && new Date(offer.validTo) < now) {
    return false;
  }
  return true;
};

const formatDiscount = (offer: IOffer) =>
  offer.discountValueType === "PERCENTAGE"
    ? `${offer.discountValue}%`
    : `£${offer.discountValue}`;

interface IFeaturedOffersProps {
  bookingType?: string;
}

const FeaturedOffers: React.FC<IFeaturedOffersProps> = ({ bookingType }) => {
  const { t } = useTranslation();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [imageUrlByOfferId, setImageUrlByOfferId] = useState<{ [offerId: string]: string }>({});

  const [loadOffers, { data: offersData }] = useLazyQuery(GET_OFFERS, {
    fetchPolicy: "network-only"
  });

  const featuredOffers = website.featuredOffers || [];

  useEffect(() => {
    if (featuredOffers.length > 0) {
      loadOffers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website.featuredOffers]);

  const offersById = useMemo(() => {
    const offers: IOffer[] = offersData?.getOffers || [];
    return offers.reduce((acc: { [id: string]: IOffer }, offer) => {
      acc[offer.id] = offer;
      return acc;
    }, {});
  }, [offersData]);

  useEffect(() => {
    featuredOffers.forEach((featured) => {
      if (featured.imageUrl && !imageUrlByOfferId[featured.offerId]) {
        getSignedUrl(featured.imageUrl).then((url) => {
          setImageUrlByOfferId((prev) => ({ ...prev, [featured.offerId]: url }));
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredOffers]);

  const visibleFeaturedOffers = featuredOffers.filter((featured) => {
    const offer = offersById[featured.offerId];
    if (!offer || !isCurrentlyValid(offer)) {
      return false;
    }
    if (!matchesBookingType(offer, bookingType)) {
      return false;
    }
    const isOrgWide = !offer.branchIds || offer.branchIds.length === 0;
    if (!selectedBranchId) {
      return isOrgWide;
    }
    return isOrgWide || offer.branchIds.includes(selectedBranchId);
  });

  if (!featuredOffers.length) {
    return null;
  }

  return (
    <Container>
      <div className="featuredOffersContainer">
        <Grid container justify="space-between" alignItems="center" className="featuredOffersHeader">
          <Grid item>
            <Typography variant="h5" className="featuredOffersTitle">
              {t("misc:exclusiveOffersAndDeals")}
            </Typography>
          </Grid>
          {website.branches && website.branches.length > 1 && (
            <Grid item>
              <Select
                displayEmpty
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value as string)}
                className="featuredOffersBranchSelect"
              >
                <MenuItem value="">{t("misc:allLocations")}</MenuItem>
                {website.branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
        </Grid>

        {visibleFeaturedOffers.length === 0 ? null : (
          <Grid container spacing={2}>
            {visibleFeaturedOffers.map((featured) => {
              const offer = offersById[featured.offerId];
              return (
                <Grid item xs={12} sm={6} md={3} key={featured.offerId}>
                  <div className="featuredOfferCard">
                    {imageUrlByOfferId[featured.offerId] && (
                      <div
                        className="featuredOfferImage"
                        style={{ backgroundImage: `url(${imageUrlByOfferId[featured.offerId]})` }}
                      >
                        <span className="featuredOfferDiscountBadge">{formatDiscount(offer)} OFF</span>
                      </div>
                    )}
                    <div className="featuredOfferBody">
                      <Typography variant="subtitle1" className="featuredOfferName">
                        {offer.name}
                      </Typography>
                      {offer.description && (
                        <Typography variant="body2" className="featuredOfferDescription">
                          {offer.description}
                        </Typography>
                      )}
                      {offer.code && (
                        <Typography variant="caption" className="featuredOfferCode">
                          {t("misc:code")}: {offer.code}
                        </Typography>
                      )}
                    </div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>
    </Container>
  );
};

export default FeaturedOffers;
